import { ComponentType } from 'react';
import { IconBaseProps } from 'react-icons/lib';
import { MdOutlineInfo } from 'react-icons/md';
import {
  Event,
  Evidence,
  EvidenceKind,
  Importance,
  Origin,
  QuoteEvidence,
  SignalKind,
  signalKindOf,
  SignalTag
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Design, DesignType, Intent, IntentType } from '@Components/designConstants';
import { DetailAction, DetailActionType } from '../activity-feed-constants';
import { getImportanceDisplay } from '../activity-feed-utils';

type BadgeDesign = DesignType | IntentType;

// bpm-types typed Evidence as `{ id; kind } & (QuoteEvidence | ...)` — so `Extract<Evidence, ...>`
// on kind returns `never` (Evidence isn't a proper discriminated union). Fall back to a manual
// intersection to widen QuoteEvidence with the outer id + kind literal.
export type QuotedEvidence = { id: string; kind: typeof EvidenceKind.QUOTE } & QuoteEvidence;
export const isQuoteEvidence = (evidence: Evidence): evidence is QuotedEvidence =>
  evidence.kind === EvidenceKind.QUOTE;

export interface SignalBadgeDisplay {
  label: string;
  design: BadgeDesign;
  icon: ComponentType<IconBaseProps>;
}

// Record<SignalTag, ...> fails to compile here until a new bpm-types SignalTag gets a suffix.
const SIGNAL_SUFFIX: Record<SignalTag, string> = {
  [SignalTag.OPPORTUNITIES]: 'Opportunity',
  [SignalTag.RELATIONSHIP_RISK]: 'Relationship Risk',
  [SignalTag.PERFORMANCE_BUSINESS_RISK]: 'Performance Risk',
  [SignalTag.NONE]: ''
};

const SIGNAL_KIND_DESIGN: Record<SignalKind, BadgeDesign> = {
  [SignalKind.POSITIVE]: Intent.SUCCESS,
  [SignalKind.RISK]: Intent.ERROR
};

// The BFF passes unrecognized wire signal tags through unchanged, so the lookup can miss at runtime.
const getSignalDisplay = (tag: SignalTag): { suffix: string; design: BadgeDesign } => {
  const suffix: string | undefined = SIGNAL_SUFFIX[tag];
  const kind = signalKindOf(tag);
  return { suffix: suffix ?? '', design: kind ? SIGNAL_KIND_DESIGN[kind] : Design.NEUTRAL };
};

export const getSignalLabel = (tag: SignalTag): string => getSignalDisplay(tag).suffix || 'None';

export const getSignalDesign = (tag: SignalTag): BadgeDesign => getSignalDisplay(tag).design;

// Compose an outlined-badge label from signalTag + importance (e.g. "High Relationship Risk").
export const getSignalBadge = (signal: SignalTag, importance: Importance): SignalBadgeDisplay => {
  const { label: importanceLabel } = getImportanceDisplay(importance);
  const { suffix, design } = getSignalDisplay(signal);
  return {
    label: `${importanceLabel} ${suffix}`.trim(),
    design,
    icon: MdOutlineInfo
  };
};

export const shouldShowChannel = (event: Event, showWip: boolean): boolean =>
  Boolean(event.channel) && (showWip || event.origin === Origin.MANUAL);

// AI-generated events keep the timestamp Oracle detected.
export const isDateEditable = (event: Event): boolean => event.origin === Origin.MANUAL;

interface DetailActionContext {
  showWip: boolean;
  isAdmin: boolean;
  origin: Origin;
}

export const getDetailActions = ({
  showWip,
  isAdmin,
  origin
}: DetailActionContext): DetailActionType[] => [
  ...(showWip ? [DetailAction.HIDE, DetailAction.CREATE_TASK] : []),
  DetailAction.DOWNLOAD,
  ...(isAdmin ? [DetailAction.EDIT] : []),
  ...(showWip && origin === Origin.MANUAL ? [DetailAction.DELETE] : [])
];

// A risk or opportunity signal fixes importance to its weight.
export const isImportanceEditable = (event: Event): boolean => event.signalTag === SignalTag.NONE;
